/**
 * Single source of truth for deal-stage color in the CRM.
 *
 * Re-exports the calm-v2 stage tokens from `@elka/theme` and adds the small
 * className/value helpers the app's consumption sites need. Replaces the
 * previously divergent definitions (filter-chip classes, DealsTable/DealsCardView
 * badge variants, dashboard pie hexes + badge classes, Timeline + detail-page maps).
 *
 * Usage rule (from the design handoff): in tables/lists a stage shows as a 6px
 * `solid` dot + plain text; the full `bg`+`fg` chip is reserved for stage rails
 * and detail-view badges.
 *
 * NOTE: the Tailwind class strings below are written out in full per stage on
 * purpose. Tailwind v4 only emits utilities whose literal class names appear in
 * scanned source, so interpolated names like `bg-stage-${stage}-soft` would be
 * silently dropped from the build. Keep these maps literal.
 */
import { stageColors, STAGE_ORDER, type DealStage, type StageColor } from '@elka/theme';

export { stageColors, STAGE_ORDER };
export type { DealStage, StageColor };

/** Open-stage progression (excludes terminal won/lost). */
export const OPEN_STAGES: DealStage[] = STAGE_ORDER.filter((s) => s !== 'won' && s !== 'lost');

/** Full stage chip/badge classes (soft bg + fg text). */
const STAGE_CHIP_CLASS: Record<DealStage, string> = {
  lead: 'bg-stage-lead-soft text-stage-lead',
  qualified: 'bg-stage-qualified-soft text-stage-qualified',
  proposal: 'bg-stage-proposal-soft text-stage-proposal',
  negotiation: 'bg-stage-negotiation-soft text-stage-negotiation',
  won: 'bg-stage-won-soft text-stage-won',
  lost: 'bg-stage-lost-soft text-stage-lost',
};

/** 6px solid stage dot / probability-bar fill background. */
const STAGE_SOLID_CLASS: Record<DealStage, string> = {
  lead: 'bg-stage-lead-solid',
  qualified: 'bg-stage-qualified-solid',
  proposal: 'bg-stage-proposal-solid',
  negotiation: 'bg-stage-negotiation-solid',
  won: 'bg-stage-won-solid',
  lost: 'bg-stage-lost-solid',
};

/** Stage label text color only (used in stacked שלב·גיל cells). */
const STAGE_TEXT_CLASS: Record<DealStage, string> = {
  lead: 'text-stage-lead',
  qualified: 'text-stage-qualified',
  proposal: 'text-stage-proposal',
  negotiation: 'text-stage-negotiation',
  won: 'text-stage-won',
  lost: 'text-stage-lost',
};

/** Selected/unselected filter-chip classes for ChipFilterBar. */
const STAGE_FILTER_SELECTED_CLASS: Record<DealStage, string> = {
  lead: 'bg-stage-lead-solid text-white',
  qualified: 'bg-stage-qualified-solid text-white',
  proposal: 'bg-stage-proposal-solid text-white',
  negotiation: 'bg-stage-negotiation-solid text-white',
  won: 'bg-stage-won-solid text-white',
  lost: 'bg-stage-lost-solid text-white',
};

const STAGE_FILTER_UNSELECTED_CLASS: Record<DealStage, string> = {
  lead: 'bg-stage-lead-soft text-stage-lead hover:opacity-80',
  qualified: 'bg-stage-qualified-soft text-stage-qualified hover:opacity-80',
  proposal: 'bg-stage-proposal-soft text-stage-proposal hover:opacity-80',
  negotiation: 'bg-stage-negotiation-soft text-stage-negotiation hover:opacity-80',
  won: 'bg-stage-won-soft text-stage-won hover:opacity-80',
  lost: 'bg-stage-lost-soft text-stage-lost hover:opacity-80',
};

/** Helpers fall back to the `lead` look for legacy/unknown stage strings.
 * Tailwind classes for a full stage chip/badge: soft bg + fg text. */
export const stageChipClass = (stage: DealStage): string =>
  STAGE_CHIP_CLASS[stage] ?? STAGE_CHIP_CLASS.lead;

/** Tailwind class for the 6px solid stage dot / probability bar fill. */
export const stageSolidClass = (stage: DealStage): string =>
  STAGE_SOLID_CLASS[stage] ?? STAGE_SOLID_CLASS.lead;

/** Tailwind class for stage label text only. */
export const stageTextClass = (stage: DealStage): string =>
  STAGE_TEXT_CLASS[stage] ?? STAGE_TEXT_CLASS.lead;

/** Filter-chip classes for a stage option. */
export const stageFilterSelectedClass = (stage: DealStage): string =>
  STAGE_FILTER_SELECTED_CLASS[stage] ?? STAGE_FILTER_SELECTED_CLASS.lead;
export const stageFilterUnselectedClass = (stage: DealStage): string =>
  STAGE_FILTER_UNSELECTED_CLASS[stage] ?? STAGE_FILTER_UNSELECTED_CLASS.lead;
